/**
 * TIM Composable
 * 向后兼容层，同时使用新的 SDK 和 Service 层
 *
 * 新代码建议直接使用:
 * - SDK: import { getTIM, createTIM } from '@/sdk/tim/index.js'
 * - Service: import { sendTextMessage, getMessageList } from '@/services/messageService.js'
 * - Event: import { eventBus, SDK_EVENTS } from '@/sdk/eventBus.js'
 */
import { getTIM, createTIM, destroyTIM, getLoginUser, isTIMReady } from '@/sdk/tim/index.js'
import { eventBus, SDK_EVENTS } from '@/sdk/eventBus.js'
import {
  initMessageListener,
  getMessageList,
  sendTextMessage,
  sendImageMessage,
  sendAudioMessage,
  sendCustomMessage,
  revokeMessage,
  resendMessage,
  setMessageRead,
  getConversationProfile
} from '@/services/messageService.js'
import { initCallListener } from '@/services/callService.js'
import { initConversationListener } from '@/services/conversationService.js'
import { useGlobalStore } from '@/stores/global.js'
import { useUserStore } from '@/stores/user.js'
import { useConversationStore } from '@/stores/conversation.js'
import router from '@/router/index.js'

// 保留原始 emitter 用于向后兼容
const listeners = {}
export const emitter = {
  on(event, fn) {
    if (!listeners[event]) listeners[event] = []
    listeners[event].push(fn)
    // 同时注册到新 eventBus
    return eventBus.on(event, fn)
  },
  off(event, fn) {
    if (!listeners[event]) return
    listeners[event] = listeners[event].filter((f) => f !== fn)
    eventBus.off(event, fn)
  },
  emit(event, data) {
    // 触发兼容层监听器
    ;(listeners[event] || []).forEach((fn) => fn(data))
    // 同时触发 eventBus（SDK 事件走这里）
    eventBus.emit(event, data)
  }
}

/**
 * 初始化 TIM SDK
 * @param {Object} credentials
 */
export async function initTIM(credentials) {
  const { imUserId, userSig, sdkappid } = credentials

  // 注册全局事件监听
  _registerGlobalEvents()

  // 初始化 SDK
  await createTIM({ sdkAppId: sdkappid, userId: imUserId, userSig })

  // 初始化消息服务监听
  initMessageListener()

  // 初始化通话信令监听
  initCallListener()

  // 初始化会话列表监听
  initConversationListener()

  // 存储凭证
  const userStore = useUserStore()
  userStore.setCredentials({ imUserId, userSig, sdkappid })

  // 主动检查并触发 SDK_READY（TIM SDK 可能在 login 返回前就触发了）
  if (isTIMReady()) {
    emitter.emit('SDK_READY')
  }

  return true
}

/**
 * 注册全局事件监听
 */
function _registerGlobalEvents() {
  const globalStore = useGlobalStore()
  const userStore = useUserStore()

  // SDK 就绪
  eventBus.on(SDK_EVENTS.SDK_READY, async () => {
    globalStore.setSdkReady(true)
    // 延迟获取个人资料，确保 SDK 完全就绪（500ms 后再调用）
    setTimeout(async () => {
      if (!isTIMReady()) {
        console.warn('[TIM] getMyProfile skipped: SDK not ready')
        return
      }
      const tim = getTIM()
      if (!tim) return
      try {
        const res = await tim.getMyProfile()
        userStore.setMyInfo(res.data)
      } catch (e) {
        if (e?.code === 3000) {
          console.warn('[TIM] getMyProfile skipped: SDK not ready')
          return
        }
        console.warn('[TIM] getMyProfile failed', e)
      }
    }, 500)
  })

  // SDK 未就绪
  eventBus.on(SDK_EVENTS.SDK_NOT_READY, () => {
    globalStore.setSdkReady(false)
  })

  // 被踢出
  eventBus.on(SDK_EVENTS.KICKED_OUT, () => {
    globalStore.setSdkReady(false)
    router.push({ name: 'Error', query: { reason: 'kicked' } })
  })
}

// ============ useTIM Composable ============

export function useTIM() {
  function _guard() {
    if (!getTIM()) throw new Error('TIM not initialized')
  }

  async function getMsgList(conversationID, nextReqMessageID = '') {
    _guard()
    return getMessageList(conversationID, nextReqMessageID)
  }

  async function sendText(conversationID, text) {
    _guard()
    return sendTextMessage(conversationID, text)
  }

  async function sendImage(conversationID, file) {
    _guard()
    return sendImageMessage(conversationID, file)
  }

  async function sendAudio(conversationID, audioBlob, duration) {
    _guard()
    return sendAudioMessage(conversationID, audioBlob, duration)
  }

  async function sendFace(conversationID, index, data) {
    _guard()
    const msg = getTIM().createFaceMessage({
      to: _getToID(conversationID),
      conversationType: getTIM().TYPES.CONV_C2C,
      payload: { index, data }
    })
    const res = await getTIM().sendMessage(msg)
    return res.data.message
  }

  async function sendCustom(conversationID, data, description = '', extension = '') {
    _guard()
    return sendCustomMessage(conversationID, data, description, extension)
  }

  async function revoke(msg) {
    _guard()
    return revokeMessage(msg)
  }

  async function resend(msg) {
    _guard()
    return resendMessage(msg)
  }

  async function setRead(conversationID) {
    return setMessageRead(conversationID)
  }

  async function getConvProfile(conversationID) {
    return getConversationProfile(conversationID)
  }

  function _getToID(conversationID) {
    return String(conversationID).replace(/^C2C/, '')
  }

  function destroy() {
    destroyTIM()
  }

  return {
    initTIM,
    getMessageList: getMsgList,
    sendTextMessage: sendText,
    sendImageMessage: sendImage,
    sendAudioMessage: sendAudio,
    sendFaceMessage: sendFace,
    sendCustomMessage: sendCustom,
    revokeMessage: revoke,
    resendMessage: resend,
    setMessageRead: setRead,
    getConversationProfile: getConvProfile,
    getTIM,
    destroy,
    emitter
  }
}

// ============ 顶层辅助函数 ============

export function timGetMyUserID() {
  return getLoginUser()
}

export async function timSetMessageRead(conversationID) {
  return setMessageRead(conversationID)
}

export async function timGetConversationProfile(conversationID) {
  return getConversationProfile(conversationID)
}

export async function timSendCustomMessage(conversationID, data, description = '', extension = '') {
  return sendCustomMessage(conversationID, data, description, extension)
}

export { isTIMReady, SDK_EVENTS, eventBus }
